<script setup lang="ts">
  import { computed, ref } from "vue";
  import { useField } from "vee-validate";
  import { ListPlus, Plus, Trash2 } from "lucide-vue-next";
  import { AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
  } from "@/components/ui/alert-dialog";
  import { Textarea } from "@/components/ui/textarea";
  import { Label } from "@/components/ui/label";
  import { Button } from "@/components/ui/button";
  import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
  import { Switch } from "@/components/ui/switch";
  import { Select } from "@/components/episto-ui/select";
  import { TagsInput } from "@/components/ui/tags-input";
  import { Tooltip } from "@/components/ui/tooltip";
  import JcdGalleryField from "./JcdGalleryField.vue";
  import type { JcdAgglomerationMode, JcdReferentials } from "@/types";

  const props = defineProps<{
    referentials: JcdReferentials | null;
  }>();

  const { value: maskedVisuals } = useField<string[]>("masked_visual_urls");
  const { value: unmaskedVisuals } = useField<string[]>("unmasked_visual_urls");
  const { value: attributionEnabled } = useField<boolean>("attribution_enabled");
  const { value: competitorBrands } = useField<string[]>("competitor_brands");
  const { value: agglomerationMode } = useField<JcdAgglomerationMode>("agglomeration_mode");
  const { value: agglomerationIds, errorMessage: agglomerationError } = useField<number[]>("jcd_agglomeration_ids");
  const { value: intents } = useField<string[]>("intents");

  const intentMaxLength = props.referentials?.intent_max_length;
  const isTooLong = (intent: string) => !!intentMaxLength && intent.length > intentMaxLength;
  const intentOptions = (props.referentials?.intents || []).map((intent) => ({ label: intent, value: intent }));
  const agglomerationOptions = (props.referentials?.agglomerations || []).map((agglomeration) => ({
    label: agglomeration.name,
    value: agglomeration.id,
  }));
  const agglomerationModeOptions = [
    { value: "all", label: "Toutes les agglomérations", description: "Aucune contrainte géographique" },
    { value: "only", label: "Uniquement certaines agglomérations", description: "Tout ce qui n'est pas dans la liste est exclu" },
    { value: "except", label: "Toutes sauf certaines agglomérations", description: "Seules celles de la liste sont exclues" },
  ];
  const agglomerationSummary = computed(() => {
    const count = agglomerationIds.value.length;
    if (!count) return null;
    if (agglomerationMode.value === "only") {
      return count === 1
        ? "Seule cette agglomération est ciblée. Tout le reste est exclu."
        : `Seules ces ${count} agglomérations sont ciblées. Tout le reste est exclu.`;
    }
    return count === 1
      ? "Cette agglomération est exclue. Tout le reste est ciblé."
      : `Ces ${count} agglomérations sont exclues. Tout le reste est ciblé.`;
  });
  const selectAgglomerationMode = (mode: JcdAgglomerationMode) => {
    agglomerationMode.value = mode;
    if (mode === "all") agglomerationIds.value = [];
  };
  const imageAccept = computed(() => props.referentials?.image_mime_types.join(",") || "image/*");

  const bulkDialogOpen = ref(false);
  const bulkBrandsText = ref("");
  const openBulkDialog = () => {
    bulkBrandsText.value = competitorBrands.value.join("\n");
    bulkDialogOpen.value = true;
  };
  const confirmBulkBrands = () => {
    competitorBrands.value = bulkBrandsText.value
      .split(/\n+/)
      .map((brand) => brand.trim())
      .filter(Boolean);
    bulkDialogOpen.value = false;
  };

  const addIntent = () => {
    intents.value = [...intents.value, ""];
  };
  const updateIntent = (index: number, value: string) => {
    intents.value = intents.value.map((intent, i) => (i === index ? value : intent));
  };
  const removeIntent = (index: number) => {
    intents.value = intents.value.filter((_, i) => i !== index);
  };
</script>

<template>
  <AccordionItem value="questionnaire">
    <AccordionTrigger class="text-base font-semibold">Questionnaire</AccordionTrigger>
    <AccordionContent class="space-y-4 px-1 pt-2">
      <JcdGalleryField
        v-model="maskedVisuals"
        label="Visuels masqués"
        :accept="imageAccept" />

      <JcdGalleryField
        v-model="unmaskedVisuals"
        label="Visuels démasqués"
        :accept="imageAccept" />

      <div class="space-y-2">
        <Label class="text-sm">Agglomérations</Label>
        <RadioGroup
          :model-value="agglomerationMode"
          class="gap-2"
          @update:model-value="selectAgglomerationMode($event as JcdAgglomerationMode)">
          <Label
            v-for="option in agglomerationModeOptions"
            :key="option.value"
            class="hover:bg-accent/50 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5 flex cursor-pointer items-start gap-3 rounded-lg border p-3 font-normal">
            <RadioGroupItem
              :value="option.value"
              class="mt-0.5" />
            <div class="space-y-0.5">
              <div class="text-sm font-medium">{{ option.label }}</div>
              <div class="text-muted-foreground text-xs">{{ option.description }}</div>
            </div>
          </Label>
        </RadioGroup>
      </div>

      <div
        v-if="agglomerationMode !== 'all'"
        class="space-y-2">
        <Label class="text-sm">{{ agglomerationMode === "only" ? "Agglomérations ciblées" : "Agglomérations exclues" }}</Label>
        <Select
          v-model="agglomerationIds"
          :options="agglomerationOptions"
          multiple
          filterable
          clearable
          placeholder="Choisir des agglomérations" />
        <p
          v-if="agglomerationError"
          class="text-destructive text-xs">
          {{ agglomerationError }}
        </p>
        <p
          v-else-if="agglomerationSummary"
          class="text-muted-foreground text-xs">
          {{ agglomerationSummary }}
        </p>
      </div>

      <Label class="flex items-start justify-between gap-4">
        <div class="space-y-0.5">
          <div class="text-sm font-medium">Mesurer l'attribution</div>
          <div class="text-muted-foreground text-xs">
            Pose la question « Selon vous, quel est le nom de la marque qui communique ? » parmi la marque et ses concurrents
          </div>
        </div>
        <Switch v-model="attributionEnabled" />
      </Label>

      <div
        v-if="attributionEnabled"
        class="space-y-2">
        <Label class="text-sm">Marques concurrentes</Label>
        <div class="flex gap-2">
          <TagsInput
            v-model="competitorBrands"
            placeholder="Ajouter une marque puis Entrée"
            class="flex-1" />
          <Tooltip content="Ajouter une liste de marques">
            <Button
              type="button"
              variant="outline"
              size="icon"
              class="shrink-0 self-start"
              @click="openBulkDialog">
              <ListPlus class="h-4 w-4" />
            </Button>
          </Tooltip>
        </div>
      </div>

      <AlertDialog v-model:open="bulkDialogOpen">
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ajouter des marques en masse</AlertDialogTitle>
            <AlertDialogDescription>Collez une liste de marques, une par ligne.</AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            v-model="bulkBrandsText"
            placeholder="Une marque par ligne…"
            class="max-h-[50vh] min-h-[150px]" />
          <AlertDialogFooter>
            <AlertDialogCancel @click="bulkDialogOpen = false">Annuler</AlertDialogCancel>
            <AlertDialogAction @click="confirmBulkBrands">OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div class="space-y-2">
        <Label class="text-sm">Intentions</Label>
        <div
          v-for="(intent, index) in intents"
          :key="index"
          class="space-y-1">
          <div class="flex gap-2">
            <Select
              :model-value="intent"
              :options="intentOptions"
              creatable
              filterable
              placeholder="Choisir ou saisir une intention"
              class="flex-1"
              @update:model-value="updateIntent(index, String($event ?? ''))" />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              class="h-9 w-9 shrink-0"
              @click="removeIntent(index)">
              <Trash2 class="h-4 w-4" />
            </Button>
          </div>
          <p
            v-if="isTooLong(intent)"
            class="text-destructive text-xs">
            {{ intent.length }} / {{ intentMaxLength }} caractères : raccourcissez cette intention
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          class="text-sm"
          @click="addIntent">
          <Plus class="mr-2 h-4 w-4" />
          Ajouter une intention
        </Button>
      </div>
    </AccordionContent>
  </AccordionItem>
</template>
